/**
 * Digital Products Account Page Component
 *
 * Displays all digital products available to the user:
 *
 * 1. Moodle E-Learning Courses:
 *    - Course title with "E-learning:" prefix
 *    - Progress indicator (progress bar at top for in-progress courses)
 *    - Status text (not started, in progress %, or completed)
 *    - License validity period
 *    - "Uitgenodigd" badge with InformationOverlay for invited users
 *    - Course image from Moodle
 *    - "Start training" / "Naar training" button
 *
 * 2. SSO Services (external applications):
 *    - Service icon and label
 *    - Description body text
 *    - External application link button
 */

import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Button, Image } from '@uncinc/uncinc-react-kitchen-sink';
import type { ThunkDispatch } from 'redux-thunk';
import type { AnyAction } from 'redux';

import { userGetCurrent } from '@uncinc/react-drupal-core';
import { fetchMoodleCourses } from '../../../../store/actions/MoodleCourses';
import {
  selectMoodleCoursesJS,
  selectMoodleCoursesLoading,
  selectMoodleCoursesError,
} from '../../../../store/selectors/MoodleCourses';
import type {
  MoodleCourse,
  CourseProgressStatus,
} from '../../../../types/moodle-courses';
import { getCourseProgressStatus } from '../../../../types/moodle-courses';
import InformationOverlay from '../../../../components/InformationOverlay';
import useCurrentAccounts from '../../../../hooks/useCurrentAccounts';

import './style.scss';

const ProductsAccountPage: React.FC = () => {
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, AnyAction>>();
  const { t } = useTranslation();

  // Get courses and state from Redux
  const courses = useSelector(selectMoodleCoursesJS);
  const isLoading = useSelector(selectMoodleCoursesLoading);
  const error = useSelector(selectMoodleCoursesError);

  // Get connected SSO services based on user roles
  const { connectedAccounts } = useCurrentAccounts();

  // Refresh user data (roles) and fetch courses on component mount
  useEffect(() => {
    dispatch(userGetCurrent());
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    dispatch(fetchMoodleCourses());
  }, [dispatch]);

  const getProgressStatus = (course: MoodleCourse): CourseProgressStatus => (
    getCourseProgressStatus(course)
  );

  /**
   * Get status label based on progress
   * - Not started: "Nog niet begonnen"
   * - In progress: "X% voltooid"
   * - Completed: "Voltooid"
   */
  const getStatusLabel = (
    course: MoodleCourse,
    status: CourseProgressStatus,
  ): string => {
    if (status === 'not_started') {
      return t('mijn_vnl.products.status_not_started');
    }
    if (status === 'completed') {
      return t('mijn_vnl.products.status_completed');
    }
    return t('mijn_vnl.products.status_in_progress', {
      percentage: course.progress,
    });
  };

  /**
   * Calculate remaining time and format the end date from enrollment_timeend.
   * Returns the formatted end date and a human-readable remaining string.
   */
  const getLicenseExpiry = (
    enrollmentTimeend: number,
  ): { endDate: string; remaining: string } => {
    const now = Date.now();
    const endDateMs = enrollmentTimeend * 1000;
    const remainingMs = endDateMs - now;
    const remainingDays = Math.max(
      0,
      Math.ceil(remainingMs / (1000 * 60 * 60 * 24)),
    );

    // Format end date as DD-MM-YYYY
    const date = new Date(endDateMs);
    const endDate = date.toLocaleDateString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

    // Build remaining time string
    let remaining: string;
    if (remainingDays < 30) {
      remaining = t('mijn_vnl.products.license_remaining_days', {
        count: remainingDays,
      });
    } else {
      const remainingMonths = Math.round(remainingDays / 30);
      remaining = t('mijn_vnl.products.license_remaining', {
        count: remainingMonths,
      });
    }

    return { endDate, remaining };
  };

  /**
   * Get user-friendly error message based on error code
   */
  const getErrorMessage = (errorCode: string): string => {
    if (errorCode === 'UNAUTHENTICATED') {
      return t('mijn_vnl.products.auth_error');
    }
    return t('mijn_vnl.products.load_error');
  };

  if (isLoading) {
    return (
      <div className="Products Products--loading">
        <p>{t('general.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="Products Products--error">
        <p>{getErrorMessage(error)}</p>
      </div>
    );
  }

  // Show empty state only if no courses AND no services
  const hasNoCourses = courses.length === 0;
  const hasNoServices = connectedAccounts.length === 0;

  if (hasNoCourses && hasNoServices) {
    return (
      <div className="Products Products--empty">
        <p>{t('mijn_vnl.products.empty')}</p>
      </div>
    );
  }

  return (
    <div className="Products">
      {/* Moodle Courses Section */}
      {courses.length > 0 && (
        <div className="Products__grid">
          {courses.map((course: MoodleCourse) => {
            const progressStatus = getProgressStatus(course);
            const statusLabel = getStatusLabel(course, progressStatus);
            const licenseExpiry = getLicenseExpiry(course.enrollment_timeend);
            const hasProgress = course.progress > 0
              && progressStatus === 'in_progress';
            const isInvited = course.enrollment_source === 'invited';

            return (
              <div
                key={course.id}
                className={`Products__card ${hasProgress ? 'Products__card--has-progress' : ''}`}
              >
                {/* Progress bar at top of card for in-progress courses */}
                {hasProgress && (
                  <div className="Products__card__progress-bar">
                    <div
                      className="Products__card__progress-fill"
                      style={{ width: `${course.progress}%` }}
                    />
                  </div>
                )}

                <div className="Products__card__inner">
                  <div className="Products__card__content">
                    <h3 className="Products__card__title">
                      {t('mijn_vnl.products.elearning_prefix')}
                      {course.fullname}
                    </h3>

                    <div className="Products__card__info">
                      {/* "Uitgenodigd" badge with InformationOverlay for invited */}
                      {isInvited && course.inviter && (
                        <div className="Products__card__invited">
                          <InformationOverlay
                            overlayKey={`invited-info-${course.id}`}
                            title={t('mijn_vnl.products.invited_overlay_title')}
                            body={t('mijn_vnl.products.invited_body')}
                            buttonTitle={t('mijn_vnl.products.invited_label')}
                            buttonClassName="Products__card__invited-badge"
                            buttonIcon="question-circle"
                          >
                            <div className="Products__invited-details">
                              <p>
                                <strong>
                                  {t('mijn_vnl.products.invited_order_number')}
                                </strong>
                                {' '}
                                #{course.inviter.order_number}
                              </p>
                              <p>
                                <strong>
                                  {t('mijn_vnl.products.invited_email')}
                                </strong>
                                {' '}
                                {course.inviter.email}
                              </p>
                              <p>
                                <strong>
                                  {t('mijn_vnl.products.invited_by')}
                                </strong>
                                {' '}
                                {course.inviter.first_name}
                                {' '}
                                {course.inviter.last_name}
                              </p>
                            </div>
                          </InformationOverlay>
                        </div>
                      )}

                      {/* Status text */}
                      <p className="Products__card__status">
                        {statusLabel}
                      </p>

                      {/* License validity */}
                      <p className="Products__card__license">
                        {t('mijn_vnl.products.license_expiry', {
                          date: licenseExpiry.endDate,
                          remaining: licenseExpiry.remaining,
                        })}
                      </p>
                    </div>

                    <div className="Products__card__actions">
                      <Button
                        href={course.url}
                      >
                        {course.progress > 0
                          ? t('mijn_vnl.products.continue_training')
                          : t('mijn_vnl.products.start_training')}
                      </Button>
                    </div>
                  </div>

                  <div className="Products__card__image">
                    <Image
                      src={course.image}
                      alt={course.fullname}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* SSO Services Section */}
      {connectedAccounts.length > 0 && (
        <div className="Products__services">
          <div className="Products__services__list">
            {connectedAccounts.map(({
              id,
              url,
              body,
              icon,
              label,
            }) => (
              <div key={id} className="Products__services__item">
                <div className="Products__services__item__header">
                  <div className="Products__services__item__icon">
                    <i className={`icon ${icon}`} />
                  </div>
                  <span className="Products__services__item__label">
                    {label}
                  </span>
                </div>
                <p className="Products__services__item__body">
                  {body}
                </p>
                <Button href={url}>
                  {t('buttons.external_application')}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsAccountPage;
